import { useMeta } from 'quasar'
import { useRoute, useRouter } from 'vue-router'

import { computed, watch } from 'vue'
import { AVAILABLE_LANG_LIST, BASE_URL } from 'src/common/config'
import { clearTrailingSlash, defaultLang, getLocale } from 'src/common/functions'
import { useI18n } from 'vue-i18n'
import getPageSeoData from 'src/apollo/queries/getPageSeoData.gql'
import getCompetition from 'src/apollo/queries/getCompetition.gql'
import getManufacturer from 'src/apollo/queries/getManufacturer.gql'
import getSportsbookSeo from 'src/apollo/queries/getSportsbookSeo.gql'
import getMarkdownContent from 'src/apollo/queries/getMarkdownContent.gql'
import competitionldJson from 'src/seo/competition'
import gameldJson from 'src/seo/game.js'
import gameGroupldJson from 'src/seo/gameGroup.js'
import gamesByManufacturerldJson from 'src/seo/gamesByManufacturer'
import { useQuery } from '@vue/apollo-composable'
import getSeoData from 'src/apollo/queries/getSeoData.gql'
import getGroup from 'src/apollo/queries/getGroup.gql'
import getGroupInfo from 'src/apollo/queries/getGroupInfo.gql'
import getManufacturerSeo from 'src/apollo/queries/getManufacturerSeo.gql'
import {
  capitalizeFirstLetter,
  changeDescriptionMeta,
  changeTitleAndClearTemplatesMeta,
  checkDataFromBack,
} from 'src/common/functions'

import { getConfig } from 'src/configs'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSsrStore } from 'src/stores/ssr'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'

const config = getConfig()

function useGameGroup(route) {
  const { result } = useQuery(getGroup, { slug: route.params.group || 'slot' })

  const groupSlug = route.params.group
  const subgroupSlug = route.params.subgroup

  const gameGroup = computed(() => result.value?.gameGroup)
  const subgroup = computed(() => {
    return gameGroup.value && subgroupSlug ? gameGroup.value.subgroups.find((el) => el.slug === subgroupSlug) : null
  })

  const titleName = computed(() => {
    if (gameGroup.value) {
      return gameGroup.value.seoData?.metaTitle ?? gameGroup.value.name
    }
    return groupSlug ? capitalizeFirstLetter(groupSlug) : ''
  })

  const createMeta = (title, description = null) => {
    const meta = {
      title,
      meta: {
        ogTitle: {
          name: 'og:title',
          content: title,
        },
      },
    }

    if (description) {
      meta.meta.description = { name: 'description', content: description }
      meta.meta.ogDescription = { name: 'og:description', content: description }
    }

    return meta
  }

  const addBotSpecificMeta = (meta, group, subgroup) => {
    if (useSystemStore().isBot) {
      meta.script = {
        ldJson: {
          type: 'application/ld+json',
          innerHTML: new gameGroupldJson({
            $route: route,
            subgroup,
            group,
          }).generate(),
        },
      }
    }
  }

  const handleSubgroupMeta = (subgroup) => {
    const title = subgroup.seoData?.metaTitle ?? subgroup.name
    const description = subgroup.seoData?.metaDescription ?? null

    const meta = createMeta(title, description)

    if (subgroup.seoData?.metaTitle) {
      meta.titleTemplate = (title) => title
      meta.meta.ogTitle.template = (title) => title
    }

    return meta
  }

  const handleGroupMeta = (group) => {
    const title = group.seoData?.metaTitle
    const description = group.seoData?.metaDescription

    const meta = createMeta(titleName.value)

    if (checkDataFromBack(title)) {
      changeTitleAndClearTemplatesMeta(meta, title)
    }
    if (checkDataFromBack(description)) {
      changeDescriptionMeta(meta, description)
    }

    return meta
  }

  const seoData = subgroup.value ? subgroup.value?.seoData : gameGroup.value?.seoData
  if (seoData) {
    useSiteInfoStore().updateSeoData(seoData)
  }

  if (subgroup.value) {
    const meta = handleSubgroupMeta(subgroup.value)
    addBotSpecificMeta(meta, gameGroup.value, subgroup.value)
    return meta
  }

  const meta = handleGroupMeta(gameGroup.value || {})
  addBotSpecificMeta(meta, gameGroup.value, null)
  return meta
}

export function useMetadata() {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const getGameObjectData = computed(() => useSsrStore().gameObjectData)
  const getGameObject = computed(() => useSsrStore().gameObject)

  const redirectLink = computed(() => {
    if (useSystemStore().alt) {
      let base = location.origin + BASE_URL
      const locale = route.params.locale
      const isDefaultLocale = locale && locale === defaultLang
      const is404Page = route.name === '404page'
      const rootPath = getConfig().groupRootPath

      if (isDefaultLocale && !is404Page) {
        return base + route.path.replace(`/${route.params.locale}`, '')
      }

      if (is404Page) {
        const catchAll = (route.params?.catchAll ?? []).filter((i) => i !== '')
        base = base + (!locale || isDefaultLocale ? '' : `/${locale}`)

        if (catchAll.length === 1) {
          const match = {
            'drops-wins': '/drops',
            guarantee: '/rule/guarantee',
            security: '/rule/security',
            antifraud: '/rule/antifraud',
          }

          const first = catchAll[0]
          if (match[first]) {
            return base + match[first]
          }

          return base + `/${rootPath}/${first}`
        }

        if (catchAll.length === 2) {
          const first = catchAll[0]
          const second = catchAll[1]

          return base + `/${rootPath}/${first}/${second}`
        }
      }
    }
    return null
  })

  watch(
    redirectLink,
    (value) => {
      if (value) {
        if (!useSystemStore().isBot) {
          return
        }

        const meta = {
          title: null,
          meta: {
            prerenderStatusCode: { name: 'prerender-status-code', content: '301' },
            prerenderHeader: {
              name: 'prerender-header',
              content: `Location: ${value}`,
            },
          },
        }

        useMeta(meta)
      }
    },
    { immediate: true }
  )

  const titleTemplate = (text) => (text ? `${text} | ${config.title}` : config.title)

  const baseMeta = computed(() => {
    const meta = {
      titleTemplate,
      meta: {
        ogTitle: {
          name: 'og:title',
          template: titleTemplate,
        },
        ogSiteName: {
          name: 'og:site_name',
          content: config.title,
        },
        ogType: {
          name: 'og:type',
          content: 'website',
        },
      },
    }

    const currentDomain = useSiteInfoStore().getCurrentDomain
    const mainDomain = useSiteInfoStore().getMainDomain

    const currentLocale = useUserAuthStore().getUserLanguage || getLocale()
    const pathWithoutLocale = route.fullPath.replace(`/${currentLocale}`, '')

    function createAlternateLangLinks(domain, langList, defaultLang) {
      return Object.fromEntries(
        langList.map((lang) => {
          const locale = lang === defaultLang ? '' : `/${lang}`
          return [
            `language_${lang}`,
            {
              rel: 'alternate',
              href: clearTrailingSlash(`https://${domain}${BASE_URL}${locale}${pathWithoutLocale}`),
              hrefLang: lang,
            },
          ]
        })
      )
    }

    const mainDomainAlternateLangLinks = createAlternateLangLinks(mainDomain, AVAILABLE_LANG_LIST, defaultLang)

    const HOSTNAME = config.seo.hostName

    const regionalDomainsAlternateLangLinks = HOSTNAME.reduce((acc, { domain, langMap }) => {
      const links = createAlternateLangLinks(
        domain,
        Object.keys(langMap),
        Object.keys(langMap).find((lang) => !langMap[lang])
      )
      Object.assign(acc, links)
      return acc
    }, {})

    function mergeAlternateLangLinks(mainLinks, ...additionalLinksArrays) {
      const mergedLinks = { ...mainLinks }

      additionalLinksArrays.forEach((additionalLinks) => {
        Object.keys(additionalLinks).forEach((key) => {
          mergedLinks[key] = additionalLinks[key]
        })
      })

      return mergedLinks
    }

    const alternateLangLinks = HOSTNAME.length
      ? mergeAlternateLangLinks(mainDomainAlternateLangLinks, regionalDomainsAlternateLangLinks)
      : mainDomainAlternateLangLinks

    const locationHostname = location.hostname
    const regionalDomain = HOSTNAME.find((i) => locationHostname === i.domain)
    let isSeoDomain = currentDomain === mainDomain || regionalDomain

    if (!isSeoDomain) {
      meta.meta.robots = {
        name: 'robots',
        template: () => {
          return 'noindex'
        },
      }
    }

    let canonicalLink = 'https://' + currentDomain + BASE_URL

    if (route.query.manufacturer) {
      meta.meta.robots = {
        name: 'robots',
        template: () => {
          return 'noindex, follow'
        },
      }

      const firstManufacturerFromURL = route.query.manufacturer.split(',')[0]
      const newRoute = router.resolve({
        name: 'GamesByManufacturer',
        params: { manufacturer: firstManufacturerFromURL },
      })
      canonicalLink += newRoute.fullPath
    } else {
      canonicalLink += route.fullPath
    }

    if (useSystemStore().main || !redirectLink.value) {
      meta.link = {
        favicon_mask: {
          rel: 'mask-icon',
          href: 'img/safari-pinned-tab.svg',
          color: config.themeColor,
        },

        canonical: {
          rel: 'canonical',
          href: clearTrailingSlash(canonicalLink),
        },
        ...alternateLangLinks,
      }
    }

    return meta
  })

  const extraMeta = computed(() => {
    useSiteInfoStore().updateSeoData(null)

    switch (route.name) {
      case 'Home': {
        const { result } = useQuery(getSeoData)
        useSiteInfoStore().updateSeoData(result.value?.siteInfo?.seoData)
        const title = useSiteInfoStore().getMetaTitle
        const description = useSiteInfoStore().getMetaDescription

        const meta = {
          title: title || t('navLinks.home'),
          meta: {
            description: { name: 'description', content: description },
            ogTitle: {
              name: 'og:title',
              content: title || t('navLinks.home'),
            },
            ogDescription: {
              name: 'og:description',
              content: description,
            },
          },
        }

        if (useSiteInfoStore().getMetaTitle) {
          meta.titleTemplate = (title) => title
          meta.meta.ogTitle.template = (title) => title
        }

        return meta
      }
      case 'apps': {
        const { result } = useQuery(getPageSeoData, {
          page: route.path.replace(`/${route.params.locale}`, '').split('/').slice(1).join('/'),
        })
        useSiteInfoStore().updateSeoData(result.value?.pageSeoData)
        const appsMeta = {
          title: result.value?.pageSeoData?.metaTitle,
          meta: {
            description: { name: 'description', content: result.value?.pageSeoData?.metaDescription },
            ogDescription: { name: 'og:description', content: result.value?.pageSeoData?.metaDescription },
            ogTitle: {
              name: 'og:title',
              content: result.value?.pageSeoData?.metaTitle,
            },
          },
        }

        if (appsMeta.title) {
          appsMeta.titleTemplate = (title) => title
          appsMeta.meta.ogTitle.template = (title) => title
        }

        return appsMeta
      }
      case 'Competition': {
        const { result } = useQuery(getCompetition, {
          id: parseInt(route.params.id),
          isGuest: !useUserAuthStore().getAuth,
        })

        const name = `${t('tournament')} - ${result.value?.competition?.name}`

        const meta = {
          title: name,
          meta: {
            description: {
              name: 'description',
              content: result.value?.competition?.shortDescription,
            },
            ogTitle: {
              name: 'og:title',
              content: name,
            },
            ogDescription: {
              name: 'og:description',
              content: result.value?.competition?.shortDescription,
            },
          },
        }

        if (useSystemStore().isBot && result.value?.competition) {
          meta.script = {
            ldJson: {
              type: 'application/ld+json',
              innerHTML: new competitionldJson({
                $route: route,
                competition: result.value?.competition,
              }).generate(),
            },
          }
        }
        return meta
      }
      case 'GamePageNew': {
        const code = getGameObject.value?.game?.manufacturer?.code
        if (!code) {
          return
        }

        const { result } = useQuery(getManufacturer, { code })

        const metaTitle = computed(() => {
          if (getGameObjectData.value) {
            return getGameObjectData.value.seoData?.metaTitle
              ? getGameObjectData.value.seoData.metaTitle
              : t('gameTitleTemplate', {
                  gameName: getGameObjectData.value.name,
                  company: config.shortTitle,
                })
          }
          return ''
        })

        const metaDesc = computed(() => {
          const gameObject = getGameObject.value?.game
          const providerName = result.value?.manufacturer?.name ?? null

          if (gameObject) {
            return gameObject.seoData?.metaDescription.length
              ? gameObject.seoData?.metaDescription
              : `${t('gameDescTemplate', {
                  gameName: gameObject.name,
                  providerName,
                })}`
          }
          return ''
        })

        const meta = {
          title: metaTitle.value,
          titleTemplate: (title) => title,
          meta: {
            description: { name: 'description', content: metaDesc.value },
            ogTitle: {
              name: 'og:title',
              content: metaTitle.value,
              template: (title) => title,
            },
            ogDescription: {
              name: 'og:description',
              content: metaDesc.value,
            },
          },
        }

        if (getGameObjectData.value?.gameId) {
          const imageLink = getGameObjectData.value.compressedImage
            ? getGameObjectData.value.compressedImage.webp.md || getGameObjectData.value.compressedImage.webp.lg
            : null

          if (imageLink) {
            const domain = process.env.CDN_DOMAIN_BACK ? process.env.CDN_DOMAIN_BACK : location.origin + '/'
            const image = `${domain}${imageLink}`

            meta.meta.ogImage = {
              name: 'og:image',
              content: image,
            }
          }

          if (useSystemStore().isBot) {
            meta.script = {
              ldJson: {
                type: 'application/ld+json',
                innerHTML: new gameldJson({
                  metaDesc: metaDesc.value,
                  getGameObjectData: getGameObjectData.value,
                  getGameObject: getGameObject.value,
                }).generate(),
              },
            }
          }
        }

        if (getGameObjectData.value.seoData) {
          useSiteInfoStore().updateSeoData(getGameObjectData.value.seoData)
        }

        return meta
      }
      case 'cashback': {
        return {
          title: t('titles.cashback'),
        }
      }
      case 'Casino': {
        const group = useQuery(getGroup, { slug: 'slot' })
        const groupInfo = useQuery(getGroupInfo, { slug: 'slot' })
        useSiteInfoStore().updateSeoData(group.value?.gameGroup?.seoData ?? null)
        const metaTitle = group.result.value?.gameGroup?.seoData?.metaTitle
        const name = groupInfo.result.value?.gameGroup?.name

        const meta = {
          title: metaTitle ?? name,
          meta: {
            ogTitle: {
              name: 'og:title',
              content: metaTitle ?? name,
            },
          },
        }

        if (route.name === 'Casino' && useSystemStore().isBot) {
          meta.meta.prerenderStatusCode = { name: 'prerender-status-code', content: '301' }
          meta.meta.prerenderHeader = {
            name: 'prerender-header',
            content: `Location: ${
              location.origin + router.resolve({ name: 'GamesByGroup', params: { group: 'slot' } }).path
            }`,
          }
        }

        if (group.result.value?.gameGroup?.seoData?.metaTitle) {
          meta.titleTemplate = (title) => title
          meta.meta.ogTitle.template = (title) => title
        }

        const description = group.result.value?.gameGroup?.seoData?.metaDescription

        if (description) {
          meta.meta.description = { name: 'description', content: description }
          meta.meta.ogDescription = { name: 'og:description', content: description }
        }

        return meta
      }
      case 'drops-wins': {
        return {
          title: t('titles.dropsWins'),
        }
      }
      case '404page': {
        return {
          title: t('titles.err404'),
          meta: {
            prerenderStatusCode: { name: 'prerender-status-code', content: '404' },
          },
        }
      }
      case 'rules': {
        return {
          title: t('titles.rules'),
          rulesBlock: 'position',
        }
      }
      case 'Favorites': {
        const title = t('favorites')

        return {
          title: title || t('navLinks.home'),
          meta: {
            ogTitle: {
              name: 'og:title',
              content: title || t('navLinks.home'),
            },
          },
        }
      }
      case 'BonusGames': {
        return {
          title: t('promoGames'),
          meta: {
            ogTitle: {
              name: 'og:title',
              content: t('promoGames'),
            },
          },
        }
      }
      case 'GamesByGroup': {
        return useGameGroup(route)
      }
      case 'GamesBySubGroup': {
        return useGameGroup(route)
      }
      case 'GamesByManufacturer': {
        const { result } = useQuery(getManufacturerSeo, { manufacturer: route.params.manufacturer })
        const manufacturer = result?.value?.manufacturer

        useSiteInfoStore().updateSeoData(manufacturer?.seoData ?? null)

        const titleName = computed(() => {
          if (!manufacturer) {
            return ''
          }
          if (manufacturer?.seoData?.metaTitle) {
            return manufacturer.seoData.metaTitle
          }
          return route.params.manufacturer ? capitalizeFirstLetter(route.params.manufacturer) : ''
        })

        const description = computed(() => {
          if (!manufacturer) {
            return ''
          }
          if (manufacturer?.seoData?.metaDescription) {
            return manufacturer.seoData.metaDescription
          }

          return route.params.manufacturer ? capitalizeFirstLetter(route.params.manufacturer) : ''
        })

        const meta = {
          title: titleName.value,
          meta: {
            ogTitle: {
              name: 'og:title',
              content: titleName.value,
            },
          },
        }

        if (useSystemStore().isBot && manufacturer) {
          meta.script = {
            ldJson: {
              type: 'application/ld+json',
              innerHTML: new gamesByManufacturerldJson({
                $route: route,
                manufacturer: manufacturer,
              }).generate(),
            },
          }
        }

        if (checkDataFromBack(description.value)) {
          changeDescriptionMeta(meta, description.value)
        }

        return meta
      }
      case 'Sports': {
        const { result } = useQuery(getSportsbookSeo)
        const getSeoData = result?.value?.sportsbookSeo
        const title = getSeoData?.metaTitle || t('navLinks.sport')
        const description = getSeoData?.metaDescription
        useSiteInfoStore().updateSeoData(getSeoData || null)

        const meta = {
          title,
          meta: {
            ogTitle: {
              name: 'og:title',
              content: title,
            },
          },
        }

        if (getSeoData?.metaTitle) {
          meta.titleTemplate = (title) => title
          meta.meta.ogTitle.template = (title) => title
        }

        if (description) {
          meta.meta.description = { name: 'description', content: description }
          meta.meta.ogDescription = {
            name: 'og:description',
            content: description,
          }
        }

        return meta
      }
      case 'rule': {
        const { result } = useQuery(getMarkdownContent, { slug: route.params.rule })
        const markdown = result.value?.markdownContent
        return { title: markdown?.title }
      }
      case 'Competitions': {
        const { result } = useQuery(getPageSeoData, {
          page: route.path.replace(`/${route.params.locale}`, '').split('/').slice(1).join('/'),
        })

        const seoData = result.value?.pageSeoData

        useSiteInfoStore().updateSeoData(seoData ?? null)

        const title = seoData?.metaTitle ? seoData?.metaTitle : t('titles.tournaments')
        let description = null

        if (seoData?.metaDescription) {
          description = seoData.metaDescription
        }

        const meta = {
          title,
          meta: {
            ogTitle: {
              name: 'og:title',
              content: title,
            },
          },
        }

        if (description) {
          meta.meta.description = { name: 'description', content: description }
          meta.meta.ogDescription = { name: 'og:description', content: description }
        }

        return meta
      }

      case 'Promotions': {
        const { result } = useQuery(getPageSeoData, {
          page: route.path.replace(`/${route.params.locale}`, '').split('/').slice(1).join('/'),
        })

        const seoData = result.value?.pageSeoData
        useSiteInfoStore().updateSeoData(seoData ?? null)
        const title = seoData?.metaTitle ? seoData?.metaTitle : t('Promotions')
        let description = null

        if (seoData?.metaDescription) {
          description = seoData.metaDescription
        }

        const meta = {
          title,
          meta: {
            ogTitle: {
              name: 'og:title',
              content: title,
            },
          },
        }

        if (description) {
          meta.meta.description = { name: 'description', content: description }
          meta.meta.ogDescription = { name: 'og:description', content: description }
        }

        return meta
      }

      case 'euro_slots': {
        return {
          title: t('euro_slot.title', { platformSite: config.platformSite }),
          meta: {
            description: {
              name: 'description',
              content: t('euro_slot.description', { platform: config.title }),
            },
          },
        }
      }
      default: {
        return {}
      }
    }
  })

  useMeta(() => baseMeta.value)
  useMeta(() => extraMeta.value)
}
